import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { Link, Redirect } from '../../shared/lib/nextCompat';
import {
  loadBlog,
  LoadBlogPayload,
  selectBlogCategories,
  selectBlogPosts,
} from '../../shared/store/blog';
import { eventHandler } from '../../../features/Tracking';
import PageHead from '../../shared/components/PageHead';
import { ExternalLink } from '../../shared/components/ExternalLink';
import BlogPostPreview from './BlogPostPreview';
import { BlogSectionContainer } from './styles';
import { TFunction, useLocale, useT } from '@spotify-internal/i18n-core';
import { getContentfulLocale } from '../../../../features/src/i18n/locales';
import { useCurrentUser } from '../../../@mrkt/useCurrentUser';
import { useRouter } from 'next/router';

const StickyNav = styled.div`
  @media (min-height: 575px) {
    position: sticky;
    top: 40px;
    margin-bottom: 110px;
  }
`;

type CategoryObject = {
  name: string;
  urlSlug: string;
  items: string[];
  offset: number;
  total: number;
};

type BlogSectionClassComponentProps = {
  loadBlog: (loadBlogPayload: LoadBlogPayload) => Promise<void>;
  blogCategories?: {
    bySlug: {
      [key: string]: CategoryObject;
    };
  };
  blogPosts?: {
    byId: {
      [key: string]: {
        description: string;
        releaseDate: string;
        title: string;
        urlSlug: string;
      };
    };
  };
  t: TFunction;
  locale: string;
  user: any;
  pathname: string;
};

type BlogSectionClassComponentState = { loading: boolean };

class BlogSectionClassComponent extends Component<
  BlogSectionClassComponentProps,
  BlogSectionClassComponentState
> {
  state = {
    loading: false,
  };

  loadMore = (category: CategoryObject) => {
    this.setState({ loading: true });
    this.props
      .loadBlog({
        category: category.urlSlug,
        smartlingLanguage: getContentfulLocale(this.props.locale),
        offset: category.offset + 10,
      })
      .then(() => {
        this.setState({ loading: false });
      });
  };

  render() {
    if (!this.props.blogCategories || !this.props.blogPosts) {
      return null;
    }

    const categorySlug = 'all';
    const category = this.props.blogCategories.bySlug[categorySlug];

    if (!category) {
      return <Redirect to="/blog/category/all" replace />;
    }

    if (!category.total) {
      return null;
    }

    return (
      <BlogSectionContainer
        className="row"
        data-testid={`blog-section-${categorySlug}`}
        key={categorySlug}
      >
        <PageHead
          title={[category.name, this.props.t('FRODOR_278509', 'News', '')]}
        />

        <div className="category-previews col-md-7">
          {category.items.map(id => {
            const post = this.props.blogPosts!.byId[id];
            return post && <BlogPostPreview {...post} key={post.urlSlug} />;
          })}

          {category.items.length < category.total && (
            <div className="text-center">
              <button
                type="button"
                className="btn btn-stroked-dark load-more"
                onClick={e => {
                  eventHandler(
                    'Blog Category',
                    'Load More',
                    this.props.user,
                    this.props.pathname,
                  )(e);
                  this.loadMore(category);
                }}
                disabled={this.state.loading}
              >
                {this.state.loading
                  ? this.props.t(
                      'FRODOR_0e2fe9',
                      'Loading...',
                      'Waiting for the loading process',
                    )
                  : this.props.t(
                      'FRODOR_3fedd3',
                      'Load More',
                      'Attempt to load more data',
                    )}
              </button>
            </div>
          )}
        </div>

        {/* Recent posts */}
        <StickyNav className="col-md-4 col-md-offset-1 col-sidebar hidden-xs hidden-sm">
          <h2 className="cta-large">
            {this.props.t(
              'FRODOR_dedbef',
              'Recent',
              'Most recently viewed pages',
            )}
          </h2>
          {this.props.blogCategories.bySlug.all.items
            .slice(0, 5)
            .map((id, index) => {
              const post = this.props.blogPosts!.byId[id];
              return (
                post && (
                  <h3 key={post.urlSlug}>
                    <Link href={`/blog/${post.urlSlug}`}>
                      <a
                        onClick={eventHandler(
                          'Blog Category',
                          `Recent Post #${index}`,
                          this.props.user,
                          this.props.pathname,
                        )}
                      >
                        {post.title}
                      </a>
                    </Link>
                  </h3>
                )
              );
            })}

          <div className="sidebar-twitter">
            <h2 className="cta-large">Twitter</h2>
            <p>
              {this.props.t(
                'FRODOR_89ab3a',
                'Keep up to date with Spotify Artist',
                '',
              )}
              <br />
              {this.props.t('FRODOR_12b4ba', 'News — Follow us on Twitter', '')}
              <br />
              <ExternalLink
                href="https://twitter.com/spotifyartists"
                label={this.props.t(
                  'FRODOR_697cc1',
                  'Blog - Social - Twitter',
                  '',
                )}
              >
                @SpotifyArtists
              </ExternalLink>
            </p>
          </div>
        </StickyNav>
      </BlogSectionContainer>
    );
  }
}

const selector = createStructuredSelector({
  blogCategories: selectBlogCategories,
  blogPosts: selectBlogPosts,
});

const actions = { loadBlog };

// Props won't be added directly into the component - they'll be added via the connect() function
// The only reason we're typing them at all is to appease TS
const BlogSection = (props: any) => {
  const t = useT();
  const locale = useLocale();
  const user = useCurrentUser();
  const router = useRouter();
  return (
    <BlogSectionClassComponent
      {...props}
      t={t}
      locale={locale}
      user={user}
      pathname={router?.pathname}
    />
  );
};

export default connect(selector, actions)(BlogSection);
